import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Avatar,
  Badge,
  Icon,
  Metric,
  SidePanel,
  cn,
  linkTextClasses,
  type IconName,
} from '@evogard/ui';
import { formatDate, fromColumn, normalizePlate } from '@evogard/domain';
import type {
  AgendaMemberDetail,
  StatementEntry,
  StatementEntryContext,
} from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { refusalOf } from './common.js';

type ServiceKind = StatementEntryContext['services'][number]['kind'];

/** O nome de cada espécie de atendimento, como a operação fala. */
// @valor — as chaves são os valores de `kind` na resposta, em pt-BR
const SERVICE_KIND_LABELS: Record<ServiceKind, string> = {
  demanda: 'Ticket',
  sinistro: 'Sinistro',
  retencao: 'Retenção',
  cobranca: 'Cobrança',
};

/**
 * O ícone e a cor de cada espécie — o mesmo ícone que o módulo tem na lateral,
 * para a pessoa reconhecer de onde o atendimento vem sem ler o rótulo. As cores
 * são as de fase, que já têm o par suave/forte medido em `theme.test.ts`.
 */
// @valor
const SERVICE_KIND_LOOK: Record<ServiceKind, { icon: IconName; className: string }> = {
  demanda: { icon: 'demandas', className: 'bg-fase-azul-suave text-fase-azul' },
  sinistro: { icon: 'escudo', className: 'bg-fase-vermelho-suave text-fase-vermelho' },
  retencao: { icon: 'retencao', className: 'bg-fase-roxo-suave text-fase-roxo' },
  cobranca: { icon: 'cobranca', className: 'bg-fase-amarelo-suave text-fase-amarelo' },
};

/**
 * A entrada em cascata do corpo — cada bloco chega 60ms depois do anterior,
 * enquanto o painel ainda desliza. `fill-mode-both` segura o bloco invisível
 * até a vez dele; quem pediu menos movimento recebe tudo parado.
 */
const REVEAL =
  'animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both motion-reduce:animate-none';

/**
 * O painel lateral de uma entrada do Extrato — quem pagou o boleto que gerou o
 * ganho, os veículos dele e os atendimentos desses veículos (19/10/2026).
 *
 * `SidePanel` e não `Modal`: é a ficha de um registro aberta por cima de uma
 * lista, a mesma forma do sinistro (`DESIGN.md`, "o painel lateral"). Na
 * largura `estreita`, porque é leitura de uma coluna só.
 *
 * ## De onde vem cada parte
 *
 * - **O valor e o boleto**: a própria linha do Extrato (`entry`) — nada é
 *   recalculado aqui, pelo mesmo motivo da linha: refazer a conta dá centavos
 *   de diferença contra a Carteira.
 * - **O caminho e os atendimentos**: `statementEntryContext`, que só responde
 *   para um boleto do Extrato de quem pergunta.
 * - **O associado e os veículos**: a ficha da Agenda (`agendaMember`), que já
 *   recorta pela carteira — o consultor vê só os veículos que são dele, decisão
 *   de 29/09/2026, repetida para este painel em 19/10/2026.
 *
 * O atendimento aparece em RESUMO para todos; o link só vem para quem tem a
 * permissão da tela de destino, e quem decide é o servidor.
 *
 * ## O desenho de 22/10/2026
 *
 * O topo é o VALOR que entrou, grande e verde — é a pergunta que levou a
 * pessoa a clicar. Abaixo, três blocos com cabeçalho de rótulo e contagem.
 * Desde 29/10/2026 o bloco de veículos mostra só o veículo da entrada: os
 * outros da carteira do associado, mesmo apagados, confundiam.
 */
export function StatementEntryPanel({
  entry,
  consultant,
  onClose,
}: {
  entry: StatementEntry | null;
  consultant: string | undefined;
  onClose: () => void;
}) {
  const { data: session } = useSessionContext();
  const tenantId = session?.currentTenant.id;
  const boletoCode = entry?.boletoCode ?? null;

  const context = useQuery({
    queryKey: ['sales', 'statement-entry', tenantId, consultant ?? 'me', boletoCode],
    enabled: boletoCode !== null,
    queryFn: async () => {
      const r = await api.sales.statementEntryContext({
        params: { boletoCode: boletoCode! },
        query: consultant === undefined ? {} : { consultant },
      });
      if (r.status !== 200)
        throw new Error(refusalOf(r, 'o que está por trás desta entrada'));
      return r.body;
    },
  });

  const memberCode = context.data?.memberCode ?? null;

  const member = useQuery({
    queryKey: ['sales', 'agenda-member', tenantId, consultant ?? 'me', memberCode],
    enabled: memberCode !== null,
    queryFn: async () => {
      const r = await api.sales.agendaMember({
        params: { code: memberCode! },
        query: consultant === undefined ? {} : { consultant },
      });
      if (r.status === 404) return null;
      if (r.status !== 200) throw new Error(refusalOf(r, 'a ficha do associado'));
      return r.body;
    },
  });

  const boletoVehicles = new Set(context.data?.vehicleCodes ?? []);
  const found = member.data;
  const detail: Extract<AgendaMemberDetail, { estado: 'medido' }> | null =
    found != null && found.estado === 'medido'
      ? (found as Extract<AgendaMemberDetail, { estado: 'medido' }>)
      : null;

  /*
   * Só o veículo DESTA entrada (29/10/2026, pedido do dono): os outros da
   * carteira confundiam quem abria a linha. A linha com placa casa pela placa;
   * a sem placa cai nos veículos que o boleto cobre.
   */
  const entryPlate = normalizePlate(entry?.plate);
  const vehicles = (detail?.vehicles ?? []).filter((v) =>
    entryPlate !== null
      ? normalizePlate(v.plate) === entryPlate
      : boletoVehicles.has(v.code),
  );
  const services = context.data?.services ?? [];
  const memberLoading = context.isPending || (memberCode !== null && member.isPending);

  return (
    <SidePanel
      open={entry !== null}
      onOpenChange={(open) => !open && onClose()}
      width="estreita"
      title={entry?.memberName ?? 'Associado sem nome no espelho'}
      descricao={
        entry === null
          ? undefined
          : `${entry.award.title}${entry.boletoCode ? ` · boleto nº ${entry.boletoCode}` : ''}`
      }
    >
      {entry !== null && (
        <div className="flex flex-col gap-6">
          <Amount entry={entry} />

          {context.isError && <Alert tone="erro">{context.error.message}</Alert>}
          {member.isError && <Alert tone="erro">{member.error.message}</Alert>}

          <Block icon="pessoa" title="Associado" delay="[animation-delay:60ms]">
            {memberLoading ? (
              <Skeleton rows={1} tall />
            ) : memberCode === null ? (
              <Empty>
                Este boleto não está no cadastro da casa, então não há ficha do associado
                para mostrar.
              </Empty>
            ) : detail === null ? (
              <Empty>
                O associado {memberCode} não tem veículo na carteira deste consultor hoje.
              </Empty>
            ) : (
              <div className="overflow-hidden rounded-grande border border-borda bg-superficie">
                <div className="flex items-center gap-3 p-3">
                  <Avatar nome={detail.member.name} className="size-11 text-sm" />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold text-texto">
                      {detail.member.name ?? 'sem nome no cadastro'}
                    </span>
                    <span className="text-xs text-texto-suave tabular-nums">
                      código {detail.member.code}
                    </span>
                  </div>
                </div>
                {/* Linha própria: o rótulo é longo, e ao lado do nome o espremeria. */}
                <div className="flex justify-end border-t border-borda-suave bg-superficie-suave px-3 py-1.5">
                  <Link
                    to="/sales/portfolio/members/$code"
                    params={{ code: detail.member.code }}
                    search={consultant === undefined ? {} : { consultant }}
                    className={linkTextClasses(
                      'inline-flex items-center gap-1 text-xs font-medium',
                    )}
                  >
                    Acessar na Carteira de Associados
                    <Icon nome="seta-direita" size={16} />
                  </Link>
                </div>
              </div>
            )}
          </Block>

          {(memberLoading || detail !== null) && (
            <Block
              icon="veiculo"
              title={
                vehicles.length > 1 ? 'Veículos desta entrada' : 'Veículo desta entrada'
              }
              count={memberLoading ? undefined : vehicles.length}
              delay="[animation-delay:120ms]"
            >
              {memberLoading ? (
                <Skeleton rows={1} />
              ) : vehicles.length === 0 ? (
                <Empty>
                  {entry.plate !== null
                    ? `A placa ${entry.plate} não está na carteira deste consultor hoje.`
                    : 'O veículo deste boleto não está na carteira deste consultor hoje.'}
                </Empty>
              ) : (
                <ul className="flex flex-col gap-2">
                  {vehicles.map((v) => (
                    <li
                      key={v.code}
                      className="flex items-center gap-3 rounded-grande border border-primaria/40 bg-primaria-suave/50 p-3"
                    >
                      <Plate plate={v.plate} />
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-sm text-texto">
                          {[v.brand, v.model].filter(Boolean).join(' ') || '—'}
                        </span>
                        {v.plan && (
                          <span className="truncate text-xs text-texto-suave">
                            {v.plan}
                          </span>
                        )}
                      </div>
                      <Badge tone="neutro" className="shrink-0">
                        {v.status.description ?? 'situação desconhecida'}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          )}

          <Block
            icon="historico"
            title="Atendimentos dos veículos deste boleto"
            count={context.isPending ? undefined : services.length}
            delay="[animation-delay:180ms]"
          >
            {context.isPending ? (
              <Skeleton rows={2} />
            ) : services.length === 0 ? (
              <Empty>
                Nenhum ticket, sinistro, retenção ou atendimento de cobrança registrado
                para estes veículos.
              </Empty>
            ) : (
              <ol className="flex flex-col">
                {services.map((s) => {
                  const look = SERVICE_KIND_LOOK[s.kind];
                  return (
                    <li
                      key={`${s.kind}-${s.id}`}
                      className="group/servico relative grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-3 pb-4 last:pb-0"
                    >
                      {/* o fio da linha do tempo, de um ícone ao seguinte */}
                      <span
                        aria-hidden
                        className="absolute top-9 bottom-0 left-[1.125rem] w-px -translate-x-1/2 bg-borda group-last/servico:hidden"
                      />
                      <span
                        aria-hidden
                        className={cn(
                          'relative grid size-9 place-items-center rounded-pilula ring-4 ring-fundo',
                          look.className,
                        )}
                      >
                        <Icon nome={look.icon} size={16} />
                      </span>
                      <div className="flex min-w-0 flex-col gap-1 rounded-grande border border-borda bg-superficie p-3">
                        <div className="flex items-center gap-2">
                          <span className="rotulo text-texto-suave">
                            {SERVICE_KIND_LABELS[s.kind]}
                          </span>
                          <span className="ml-auto text-xs text-texto-tenue tabular-nums">
                            {formatDate(s.openedAt.slice(0, 10))}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-texto">
                          {s.title ?? s.reference ?? `nº ${s.id}`}
                        </span>
                        {(s.status !== null || s.link !== null) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {s.status !== null && <Badge tone="neutro">{s.status}</Badge>}
                            {s.link !== null && (
                              <a
                                href={s.link}
                                className={linkTextClasses(
                                  'ml-auto inline-flex items-center gap-1 text-xs font-medium',
                                )}
                              >
                                Abrir
                                <Icon nome="seta-direita" size={16} />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Block>
        </div>
      )}
    </SidePanel>
  );
}

/**
 * O valor que ENTROU, no alto do painel — a resposta para o clique.
 *
 * Verde só quando é maior que zero, a mesma regra do `Incoming` da lista: um
 * "+ R$ 0,00" verde diria que alguma coisa entrou. Embaixo, os fatos do boleto
 * que a linha já trazia: só a data do pagamento e o valor do boleto. A tarifa
 * do banco continua no contrato e no cálculo, mas não aparece para o consultor.
 */
function Amount({ entry }: { entry: StatementEntry }) {
  // Ilegível cai no caminho do zero, sem sinal — quem escreve o número é `MoneyValue`.
  let cents: number | null;
  try {
    cents = fromColumn(entry.amount);
  } catch {
    cents = null;
  }
  const entered = cents !== null && cents > 0;
  const facts: { label: string; value: ReactNode }[] = [
    {
      label: 'Pago em',
      value: entry.paidOn === null ? '—' : formatDate(entry.paidOn),
    },
    { label: 'Valor do boleto', value: <MoneyValue value={entry.gross} /> },
  ];

  return (
    <section
      aria-label="O que esta entrada rendeu"
      className={cn(
        'relative overflow-hidden rounded-grande border border-borda bg-superficie',
        REVEAL,
      )}
    >
      {/* a faixa de cor da esquerda — o assunto é dinheiro */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 w-1',
          entered ? 'bg-sucesso' : 'bg-borda-forte',
        )}
      />
      <div className="flex items-start gap-3 px-5 pt-4 pb-3">
        {/* `Metric` e não um número à mão: a escala do valor é uma só na interna. */}
        <dl className="min-w-0 flex-1">
          <Metric
            label="Entrou no extrato"
            className={entered ? 'text-sucesso' : 'text-texto'}
            value={
              <>
                {entered && '+ '}
                <MoneyValue value={entry.amount} />
              </>
            }
            support={<span className="text-texto">{entry.award.title}</span>}
          />
        </dl>
        <span
          aria-hidden
          className={cn(
            'grid size-11 shrink-0 place-items-center rounded-grande',
            entered
              ? 'bg-sucesso-suave text-sucesso'
              : 'bg-superficie-suave text-texto-suave',
          )}
        >
          <Icon nome="dinheiro" size={20} />
        </span>
      </div>

      <dl className="grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] border-t border-borda-suave bg-superficie-suave">
        {facts.map((f) => (
          <div
            key={f.label}
            className="flex flex-col gap-0.5 border-r border-borda-suave px-5 py-2.5 last:border-r-0"
          >
            <dt className="text-xs text-texto-suave">{f.label}</dt>
            <dd className="text-sm font-medium text-texto tabular-nums">{f.value}</dd>
          </div>
        ))}
      </dl>

      {(entry.plate !== null || entry.products) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-borda-suave px-5 py-2.5">
          {entry.plate !== null && <Plate plate={entry.plate} />}
          {entry.kind === 'produto' && entry.products && (
            <span className="min-w-0 truncate text-xs text-texto-suave">
              {entry.products}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

/** Um bloco do painel: o rótulo com ícone e a contagem, e o conteúdo embaixo. */
function Block({
  icon,
  title,
  count,
  delay,
  children,
}: {
  icon: IconName;
  title: string;
  count?: number;
  /** O atraso da cascata — uma classe inteira, para o Tailwind enxergá-la. */
  delay: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('flex flex-col gap-2.5', REVEAL, delay)}>
      <h3 className="flex items-center gap-2 text-texto-suave">
        <Icon nome={icon} size={16} className="shrink-0" />
        <span className="rotulo">{title}</span>
        {count !== undefined && (
          <span className="grid h-5 min-w-5 place-items-center rounded-pilula bg-superficie-suave px-1.5 text-xs font-semibold text-texto tabular-nums">
            {count}
          </span>
        )}
        <span aria-hidden className="h-px flex-1 bg-borda-suave" />
      </h3>
      {children}
    </section>
  );
}

/**
 * A placa desenhada como placa — a faixa azul do padrão Mercosul em cima e o
 * texto em mono espaçado. É o que a pessoa procura numa lista de veículos, e
 * reconhecer a forma é mais rápido do que ler.
 */
function Plate({ plate }: { plate: string | null }) {
  return (
    <span className="inline-flex shrink-0 flex-col overflow-hidden rounded-pequeno border border-borda-forte bg-superficie text-center">
      <span aria-hidden className="h-1 bg-fase-azul" />
      <span className="px-2 py-0.5 font-mono text-sm font-semibold tracking-[0.12em] text-texto">
        {plate ?? 'sem placa'}
      </span>
    </span>
  );
}

/** O estado vazio de um bloco — frase inteira, em caixa tracejada. */
function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-grande border border-dashed border-borda px-4 py-3 text-sm text-texto-suave">
      {children}
    </p>
  );
}

/** O esqueleto enquanto a ficha chega — a forma do que vem, sem texto inventado. */
function Skeleton({ rows, tall = false }: { rows: number; tall?: boolean }) {
  return (
    <div aria-busy className="flex flex-col gap-2">
      <span className="sr-only">Carregando…</span>
      {Array.from({ length: rows }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className={cn('esqueleto block rounded-grande', tall ? 'h-[4.25rem]' : 'h-14')}
        />
      ))}
    </div>
  );
}
